import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SingleSelect } from '@components/ui/SingleSelect/SingleSelect';
import type { ISelectionOption } from '@components/Pages/AnalyticsPage/analytics.types';
import type { IFinanceFilters, IFinanceSummary } from '@interfaces/IFinance';
import { fmtUsdNoCents, fmtPct1 } from '../format';
import styles from './SummaryRail.module.css';

interface ISummaryRailProps {
  summary: IFinanceSummary;
  filters: IFinanceFilters;
}

export function SummaryRail({ summary, filters }: ISummaryRailProps) {
  const { t } = useTranslation();
  const [floor, setFloor] = useState<string>('all');
  const [trade, setTrade] = useState<string>('all');
  const [contractor, setContractor] = useState<string>('all');
  const [range, setRange] = useState<string>('this-month');

  const floorOptions: ISelectionOption[] = filters.floors.map((f) =>
    f === 'All Floors' ? { id: 'all', label: f } : { id: f.toLowerCase(), label: f },
  );

  return (
    <aside className={styles.rail}>
      <div className={styles.card}>
        <div className={styles.cardTitle}>{t('finance.rail.summary')}</div>

        <Stat
          icon="$"
          tone="navy"
          label={t('finance.rail.totalSpendToday')}
          value={fmtUsdNoCents(summary.totalSpendToday)}
        />
        <Stat
          icon="↗"
          tone="green"
          label={t('finance.rail.budgetVsActual')}
          value={fmtPct1(summary.budgetVsActualPct)}
        />
        <Stat
          icon="▦"
          tone="amber"
          label={t('finance.rail.outstandingInvoices')}
          value={String(summary.outstandingInvoices)}
        />
        <Stat
          icon="▭"
          tone="red"
          label={t('finance.rail.pendingPayments')}
          value={String(summary.pendingPayments)}
        />
        <Stat
          icon="⛟"
          tone="blue"
          label={t('finance.rail.activeOrders')}
          value={String(summary.activeOrders)}
        />
      </div>

      <div className={styles.filters}>
        <div className={styles.cardTitle}>{t('finance.rail.filters')}</div>

        <label className={styles.label}>
          <span>{t('finance.rail.floor')}</span>
          <SingleSelect
            value={floor}
            onChange={setFloor}
            options={floorOptions}
            placeholder={floorOptions[0]?.label ?? ''}
            ariaLabel={t('finance.rail.floorAria')}
          />
        </label>

        <label className={styles.label}>
          <span>{t('finance.rail.trade')}</span>
          <SingleSelect
            value={trade}
            onChange={setTrade}
            options={filters.trades}
            placeholder={filters.trades[0]?.label ?? ''}
            ariaLabel={t('finance.rail.tradeAria')}
          />
        </label>

        <label className={styles.label}>
          <span>{t('finance.rail.contractor')}</span>
          <SingleSelect
            value={contractor}
            onChange={setContractor}
            options={filters.contractors}
            placeholder={filters.contractors[0]?.label ?? ''}
            ariaLabel={t('finance.rail.contractorAria')}
          />
        </label>

        <label className={styles.label}>
          <span>{t('finance.rail.timeRange')}</span>
          <SingleSelect
            value={range}
            onChange={setRange}
            options={filters.timeRanges}
            placeholder={filters.timeRanges[0]?.label ?? ''}
            ariaLabel={t('finance.rail.timeRangeAria')}
          />
        </label>
      </div>
    </aside>
  );
}

interface IStatProps {
  icon: string;
  tone: 'navy' | 'green' | 'amber' | 'red' | 'blue';
  label: string;
  value: string;
}

function Stat({ icon, tone, label, value }: IStatProps) {
  return (
    <div className={styles.stat}>
      <span className={`${styles.statIcon} ${styles[`tone_${tone}`]}`} aria-hidden="true">
        {icon}
      </span>
      <div className={styles.statText}>
        <span className={styles.statLabel}>{label}</span>
        <span className={styles.statValue}>{value}</span>
      </div>
    </div>
  );
}
